import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import CourseHead from './CourseHead';
import CourseProgress from './CourseProgress';
import Popover from '../Finances/Popover/Popover';
import useCountdown from '../Finances/hooks/useCountdown';
import { ArrowRight, Chevron, InfoIcon, LayersIcon, WalletIcon } from './icons';
import { lessonsWord } from '../../utils/lessonsWord';

const InfoTip = ({ text }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="fv2-stat__info"
        aria-label="Пояснення"
        onClick={() => setOpen((value) => !value)}
      >
        <InfoIcon />
      </button>
      {open && <Popover anchorRef={anchorRef} onClose={() => setOpen(false)}>{text}</Popover>}
    </>
  );
};

InfoTip.propTypes = { text: PropTypes.string.isRequired };

const balanceNote = (total) => {
  if (total > 0) {
    return 'Переплата — врахується в наступній оплаті';
  }
  if (total < 0) {
    return 'Є заборгованість';
  }

  return 'Немає переплати';
};

const FinanceCourseCard = ({ course, onAccounts, onHistory, onFullCourse, fullCourseLoading }) => {
  const qty = Number(course.qty) || 0;
  const total = Number(course.total) || 0;
  const left = Number(course.lessons_left) || 0;
  const isRequest = Number(course.status) === 3;
  const canPay = !isRequest;

  const { compact } = useCountdown(course.cancellation_lesson_date);
  const qtyInfo = course.cancellation_lesson_date
    ? `Скільки оплачених занять у вас ще є. Заняття будуть анульовані згідно з Договором через ${compact}.`
    : 'Скільки оплачених занять у вас ще є на цьому курсі.';

  const openCard = (event) => {
    if (!canPay || event.target.closest('button, a, input, [role="button"]')) {
      return;
    }
    onAccounts();
  };

  return (
    <article className={`fv2-card ${canPay ? 'fv2-card--clickable' : ''}`.trim()} onClick={openCard}>
      <CourseHead
        image={course.flag || course.image}
        title={course.direction_name || course.name}
        programName={course.program_name}
        type={course.type}
        onClick={canPay ? onAccounts : null}
        actionLabel="Рахунки"
      >
        {canPay && (
          <span className="fv2-head__go" aria-hidden="true">
            <Chevron />
          </span>
        )}
      </CourseHead>

      <CourseProgress
        total={Number(course.lessons_total) || 0}
        passed={Number(course.lessons_passed) || 0}
        left={left}
      />

      <div className="fv2-stats">
        <div className="fv2-stat fv2-stat--green">
          <div className="fv2-stat__top">
            <span className="fv2-stat__icon"><LayersIcon /></span>
            <span className="fv2-stat__label">Залишок занять</span>
            <InfoTip text={qtyInfo} />
          </div>
          <b className="fv2-stat__value">{qty}</b>
          <span className="fv2-stat__note">
            {course.cancellation_lesson_date
              ? `згорять через ${compact}`
              : `оплачених ${qty === 1 ? 'заняття' : 'занять'} доступно`}
          </span>
        </div>

        <div className="fv2-stat fv2-stat--violet">
          <div className="fv2-stat__top">
            <span className="fv2-stat__icon"><WalletIcon /></span>
            <span className="fv2-stat__label">Баланс</span>
            <InfoTip text="Гроші на балансі курсу, які ще не перетворились на заняття (переплата або борг)." />
          </div>
          <b className="fv2-stat__value">{Number(course.total).toFixed(2)} грн</b>
          <span className="fv2-stat__note">{balanceNote(total)}</span>
        </div>
      </div>

      <div className="fv2-actions">
        {canPay && left > 0 && (
          <button type="button" className="v2-btn v2-btn--violet" disabled={fullCourseLoading} onClick={onFullCourse}>
            {fullCourseLoading ? 'Створюємо рахунок…' : `Оплатити до кінця курсу · ${left} ${lessonsWord(left)}`}
            {!fullCourseLoading && <ArrowRight />}
          </button>
        )}
        {canPay && (
          <button type="button" className="v2-btn v2-btn--green" onClick={onAccounts}>
            Поповнити баланс
          </button>
        )}
        {isRequest && <p className="fv2-actions__note">Оплата відкриється після підтвердження запиту менеджером.</p>}
        <button type="button" className="v2-link" onClick={onHistory}>Історія оплат і занять</button>
      </div>
    </article>
  );
};

FinanceCourseCard.propTypes = {
  course: PropTypes.object.isRequired,
  onAccounts: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
  onFullCourse: PropTypes.func.isRequired,
  fullCourseLoading: PropTypes.bool,
};

FinanceCourseCard.defaultProps = {
  fullCourseLoading: false,
};

export default FinanceCourseCard;
